import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';

import { Alert } from '@/components/ui/Alert';
import { CHANNEL_LABEL } from '@/constants/messaging';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import type {
  BroadcastAudienceType,
  BroadcastDateRange,
  BroadcastRecipientKind,
  BroadcastStatus,
  BroadcastSummary,
  MessageChannel,
  SendBroadcastPayload,
} from '@/types/messaging';
import { notificationsApi } from '../api/notifications.api';
import { NotificationHistoryCard, type HistoryFilters } from '../components/NotificationHistoryCard';
import { NotificationMetricCards } from '../components/NotificationMetricCards';
import { SendNotificationCard, MESSAGE_MAX_LENGTH } from '../components/SendNotificationCard';
import { NotificationDetailView } from '../components/NotificationDetailView';

/** How many sends one page of the history table holds. */
const HISTORY_PAGE_SIZE = 10;

/** The history table's filters and page, as they sit in the address bar. */
type Filters = Pick<HistoryFilters, keyof HistoryFilters> & { page: number };

const EMPTY_FILTERS: Filters = {
  dateRange: 'ALL',
  channel: 'ALL',
  templateKey: 'ALL',
  status: 'ALL',
  includes: 'ALL',
  sectionUuid: 'ALL',
  search: '',
  page: 1,
};

/**
 * What a link may set. The server's sets have no shared list on this side, so
 * each is written as a record over its type: the compiler then refuses one
 * that misses a value or invents one. Templates and sections come from the
 * school and are not listed.
 */
const FILTER_OPTIONS = {
  allowed: {
    dateRange: Object.keys({
      ALL: true,
      LAST_7_DAYS: true,
      LAST_30_DAYS: true,
      THIS_MONTH: true,
      PREVIOUS_MONTH: true,
    } satisfies Record<BroadcastDateRange, true>),
    channel: ['ALL', ...Object.keys(CHANNEL_LABEL)],
    status: [
      'ALL',
      ...Object.keys({ SENT: true, PARTIAL: true, FAILED: true } satisfies Record<
        BroadcastStatus,
        true
      >),
    ],
    includes: [
      'ALL',
      ...Object.keys({ STUDENT: true, PARENT: true, TEACHER: true, STAFF: true } satisfies Record<
        BroadcastRecipientKind,
        true
      >),
    ],
  },
};

/** The compose card's three recipient buttons, in the server's vocabulary. */
const AUDIENCE_TYPE: Record<'ALL' | 'SECTION' | 'INDIVIDUAL', BroadcastAudienceType> = {
  ALL: 'ALL_MEMBERS',
  SECTION: 'SECTION',
  INDIVIDUAL: 'INDIVIDUAL',
};

/**
 * Notifications and broadcasts — what the office sends out, and what arrived.
 *
 * Three reads and one write, all through `notifications.api.ts` (CLAUDE.md §6):
 * the figures above the card, who a send would reach, the history below it, and
 * the send itself. Each of the three fails independently — a slow history must
 * not stop the office writing a message, and an unreadable audience must not
 * blank the record of what was sent yesterday.
 *
 * `/admin/notifications/:id` opens one send's delivery log directly, so a row
 * can be linked to and a browser refresh lands back on it.
 */
export function NotificationsPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { search: locationSearch } = useLocation();

  // --- the compose card ------------------------------------------------------
  const [selectedChannel, setSelectedChannel] = useState<MessageChannel>('SMS');
  const [recipientType, setRecipientType] = useState<'ALL' | 'SECTION' | 'INDIVIDUAL'>('ALL');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [includeStudents, setIncludeStudents] = useState(false);
  const [includeParents, setIncludeParents] = useState(true);
  const [includeTeachers, setIncludeTeachers] = useState(false);
  const [includeStaff, setIncludeStaff] = useState(false);
  const [template, setTemplate] = useState('');
  const [message, setMessage] = useState('');
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [isSending, setIsSending] = useState(false);

  // --- the history table -----------------------------------------------------
  const [filters, setFilters] = useUrlFilters(EMPTY_FILTERS, FILTER_OPTIONS);
  const { page } = filters;

  // One request when typing stops, not one per keystroke.
  const debouncedSearch = useDebouncedValue(filters.search, 300);

  /**
   * The audience preview, narrowed to whatever the office has picked.
   *
   * Re-read when the section changes so the count under the card follows the
   * choice — "35 can be reached" must mean 35 in *this* section, not 812 on the
   * whole roll.
   */
  const sectionScope = recipientType === 'SECTION' && selectedSection ? selectedSection : undefined;

  const {
    data: audience,
    isLoading: isLoadingAudience,
    error: audienceError,
  } = useApiQuery(() => notificationsApi.audience(sectionScope), [sectionScope]);

  const {
    data: metrics,
    isLoading: isLoadingMetrics,
    refetch: refetchMetrics,
  } = useApiQuery(() => notificationsApi.metrics(), []);

  const {
    data: history,
    isLoading: isLoadingHistory,
    error: historyError,
    refetch: refetchHistory,
  } = useApiQuery(
    () =>
      notificationsApi.list({
        page,
        limit: HISTORY_PAGE_SIZE,
        search: debouncedSearch.trim() || undefined,
        channel: filters.channel === 'ALL' ? undefined : filters.channel,
        status: filters.status === 'ALL' ? undefined : filters.status,
        templateKey: filters.templateKey === 'ALL' ? undefined : filters.templateKey,
        includes: filters.includes === 'ALL' ? undefined : filters.includes,
        sectionUuid: filters.sectionUuid === 'ALL' ? undefined : filters.sectionUuid,
        dateRange: filters.dateRange,
      }),
    [
      page,
      debouncedSearch,
      filters.channel,
      filters.status,
      filters.templateKey,
      filters.includes,
      filters.sectionUuid,
      filters.dateRange,
    ],
  );

  /** The send whose delivery log is open, read from the URL. */
  const {
    data: opened,
    isLoading: isLoadingOpened,
    error: openedError,
    refetch: refetchOpened,
  } = useApiQuery(
    () => (id ? notificationsApi.get(id) : Promise.resolve(null)),
    [id],
  );

  /** Choosing a class clears the section under it — 5-A is not a section of 6. */
  useEffect(() => {
    setSelectedSection('');
  }, [selectedClass]);

  /**
   * The toggles the server refuses in a given mode are turned off as the mode
   * changes, rather than left ticked to fail on submit.
   */
  useEffect(() => {
    if (recipientType !== 'ALL') setIncludeStaff(false);
    if (recipientType === 'INDIVIDUAL') setIncludeTeachers(false);
  }, [recipientType]);

  /**
   * A filter change with the reader on page 4 would ask for a page that is gone,
   * so it goes back to the first in the same write. Not an effect on the
   * filters: that would also fire on arrival and throw away a restored page.
   */
  const setFilter = useCallback(
    <K extends keyof HistoryFilters>(key: K, value: HistoryFilters[K]) => {
      const patch: Partial<Filters> = { page: 1 };
      patch[key] = value;
      setFilters(patch);
    },
    [setFilters],
  );

  const clearFilters = useCallback(() => setFilters(EMPTY_FILTERS), [setFilters]);

  const setPage = useCallback((next: number) => setFilters({ page: next }), [setFilters]);

  const toggleStudent = useCallback((uuid: string) => {
    setChosen((current) => {
      const next = new Set(current);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  }, []);

  const selectAllStudents = useCallback((uuids: string[], next: boolean) => {
    setChosen((current) => {
      const updated = new Set(current);
      for (const uuid of uuids) {
        if (next) updated.add(uuid);
        else updated.delete(uuid);
      }
      return updated;
    });
  }, []);

  // The history's filters travel with the log and back: the page stays mounted
  // across the two addresses, so it would not look for remembered ones again.
  function openNotification(row: BroadcastSummary) {
    navigate(`/admin/notifications/${row.uuid}${locationSearch}`);
  }

  function backToHistory() {
    navigate(`/admin/notifications${locationSearch}`);
  }

  /** What is wrong with the form, in the order the office would fix it. */
  const sendProblem = useMemo(() => {
    if (!message.trim()) return 'Please enter a message before sending.';
    if (message.length > MESSAGE_MAX_LENGTH) {
      return `Keep it under ${MESSAGE_MAX_LENGTH} characters — this is ${message.length}.`;
    }
    if (!includeStudents && !includeParents && !includeTeachers && !includeStaff) {
      return 'Choose at least one group to include.';
    }
    if (recipientType === 'SECTION' && !selectedSection) {
      return 'Choose the class and section to send to.';
    }
    if (recipientType === 'INDIVIDUAL' && chosen.size === 0) {
      return 'Choose at least one student.';
    }
    return null;
  }, [
    message,
    includeStudents,
    includeParents,
    includeTeachers,
    includeStaff,
    recipientType,
    selectedSection,
    chosen.size,
  ]);

  async function handleSendNotification() {
    if (sendProblem) {
      toast.error('Not ready to send', sendProblem);
      return;
    }

    const payload: SendBroadcastPayload = {
      channel: selectedChannel,
      message: message.trim(),
      audienceType: AUDIENCE_TYPE[recipientType],
      includeStudents,
      includeParents,
      includeTeachers,
      includeStaff,
      ...(recipientType === 'SECTION' && { sectionUuid: selectedSection }),
      ...(recipientType === 'INDIVIDUAL' && { studentUuids: [...chosen] }),
      ...(template && { templateKey: template }),
    };

    setIsSending(true);

    try {
      const result = await notificationsApi.send(payload);

      if (result.failed === 0) {
        toast.success(
          `Sent to ${result.sent} ${result.sent === 1 ? 'person' : 'people'}`,
          result.skipped > 0
            ? `${result.skipped} had no mobile number on file.`
            : 'Everyone in that audience was reached.',
        );
      } else {
        toast.error(
          `${result.failed} could not be reached`,
          `${result.sent} went out. Open the send to see who failed and why.`,
        );
      }

      // A message cannot be unsent, so the box is cleared only once the server
      // has confirmed it went — a failed send must leave the text where it is.
      setMessage('');
      setTemplate('');
      setChosen(new Set());

      // The figures and the history both changed with this send.
      refetchMetrics();
      setPage(1);
      refetchHistory();
    } catch (caught) {
      toast.error(
        'Could not send',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSending(false);
    }
  }

  if (id) {
    return (
      <NotificationDetailView
        uuid={id}
        data={opened}
        isLoading={isLoadingOpened}
        error={openedError}
        onRetry={refetchOpened}
        onBack={backToHistory}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6 text-slate-900 dark:text-slate-100 w-full max-w-full overflow-hidden">
      {/* Page Header */}
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">

          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-700 dark:text-white">
              Notifications &amp; Broadcasts
            </h1>
            <p className="text-xs sm:text-sm font-bold text-slate-500 dark:text-slate-400 mt-0.5">
              Send SMS/WhatsApp updates to students, parents, and teachers.
            </p>
          </div>
        </div>
      </header>

      {/* The compose card is useless without the roll behind it, so this is the
          one failure the page states outright rather than leaving to a toast on
          submit. The history below still renders. */}
      {audienceError && (
        <Alert tone="critical" title="Could not load who this would reach">
          {audienceError.message}
        </Alert>
      )}

      {/* Top Metric Cards Section Component */}
      <NotificationMetricCards metrics={metrics} isLoading={isLoadingMetrics} />

      {/* Send Notification Card Section Component */}
      <SendNotificationCard
        selectedChannel={selectedChannel}
        setSelectedChannel={setSelectedChannel}
        recipientType={recipientType}
        setRecipientType={setRecipientType}
        selectedClass={selectedClass}
        setSelectedClass={setSelectedClass}
        selectedSection={selectedSection}
        setSelectedSection={setSelectedSection}
        includeStudents={includeStudents}
        setIncludeStudents={setIncludeStudents}
        includeParents={includeParents}
        setIncludeParents={setIncludeParents}
        includeTeachers={includeTeachers}
        setIncludeTeachers={setIncludeTeachers}
        includeStaff={includeStaff}
        setIncludeStaff={setIncludeStaff}
        template={template}
        setTemplate={setTemplate}
        message={message}
        setMessage={setMessage}
        isSending={isSending}
        handleSendNotification={handleSendNotification}
        audience={audience}
        isLoadingAudience={isLoadingAudience}
        chosen={chosen}
        onToggleStudent={toggleStudent}
        onSelectAllStudents={selectAllStudents}
      />

      {/* Notification History Card Section Component */}
      <NotificationHistoryCard
        filters={filters}
        setFilter={setFilter}
        clearFilters={clearFilters}
        history={history?.items ?? []}
        total={history?.total ?? 0}
        page={page}
        limit={HISTORY_PAGE_SIZE}
        setPage={setPage}
        isLoading={isLoadingHistory}
        error={historyError}
        onRetry={refetchHistory}
        onSelectNotification={openNotification}
        classes={audience?.classes ?? []}
        templates={audience?.templates ?? []}
      />
    </div>
  );
}
